import { FiMic } from 'react-icons/fi';

export const abstractTopicsData = {
  courseId: 'abstract-topics',
  courseType: 'speaking-course',
  title: 'Abstract Topics',
  icon: FiMic,
  overview: {
    impact: 'Handle complex Part 3 questions about society, culture, education, and the environment with confidence and depth',
    focus: 'Structuring abstract answers, giving balanced viewpoints, and using hypothetical language',
    uses: 'Speaking Part 3 — opinion and discussion questions on broad themes',
    duration: '25 minutes',
  },

  sections: [
    {
      title: 'Understanding Abstract Questions',
      content: `<p>In Part 3 the examiner moves from personal questions to <strong>abstract, society-level</strong> topics. Instead of "Do you like reading?" you might hear "Why do you think people read less nowadays?" These questions test whether you can discuss ideas, not just describe experiences.</p>
<p>Abstract questions typically fall into predictable themes:</p>`,
      points: [
        'Society & social change — e.g., "How has technology changed the way people interact?"',
        'Culture & tradition — e.g., "Should traditional customs be preserved even if they seem outdated?"',
        'Education — e.g., "Is university education still important in the modern economy?"',
        'Environment — e.g., "Whose responsibility is it to solve environmental problems?"',
        'Work & economy — e.g., "Do you think remote working will become the norm?"',
      ],
      examples: [
        { label: 'Concrete (Part 1)', text: 'I usually read novels before bed.' },
        { label: 'Abstract (Part 3)', text: 'I believe reading rates have declined because digital entertainment offers instant gratification, whereas books require sustained concentration.' },
      ],
    },
    {
      title: 'Structuring Your Answer',
      content: `<p>A well-structured abstract answer follows a clear pattern: <strong>Position → Reason → Example → Balance</strong>. This framework keeps you fluent and organised, even when the topic is unfamiliar.</p>`,
      points: [
        'Position: State your view clearly — "I believe that...", "In my opinion...", "From my perspective..."',
        'Reason: Explain why — "The main reason is that...", "This is largely because..."',
        'Example: Illustrate — "For instance, in my country...", "A good example would be..."',
        'Balance: Acknowledge the other side — "Having said that...", "On the other hand...", "Although some people argue that..."',
      ],
      examples: [
        { label: 'Full structure', text: 'I believe university education is still valuable (position) because it develops critical thinking skills that employers value (reason). For instance, my sister\'s philosophy degree helped her land a consulting job (example). Having said that, I can see why some people prefer vocational training for more practical careers (balance).' },
      ],
      warning: 'Avoid one-sentence answers. Even if you agree strongly, always provide a reason and an example before optionally balancing.',
    },
    {
      title: 'Hypothetical Language',
      content: `<p>Many Part 3 questions ask you to speculate: "What would happen if...?", "How might things change in the future?" Using <strong>hypothetical structures</strong> correctly signals grammatical range — a key Band 7+ criterion.</p>`,
      points: [
        '"If governments invested more in renewable energy, pollution levels would drop significantly."',
        '"Suppose everyone had access to free education — I think society would become more equal."',
        '"Imagine a world without social media; people might communicate more face to face."',
        '"Were the government to ban single-use plastics, companies would be forced to innovate." (formal inversion)',
        '"It\'s possible that artificial intelligence could replace many routine jobs within the next decade."',
      ],
      table: {
        headers: ['Structure', 'Example', 'When to Use'],
        rows: [
          ['If + past simple, would + verb', 'If schools taught coding, students would be better prepared.', 'Standard hypothetical'],
          ['Suppose / Imagine + clause', 'Suppose petrol prices doubled — people would use public transport more.', 'Introducing a scenario'],
          ['Were + subject + to + verb', 'Were the government to act sooner, the damage would be less severe.', 'Formal / written-style speaking'],
          ['It\'s possible / likely that ...', 'It\'s likely that remote work will continue to grow.', 'Softer speculation'],
          ['I would argue that ...', 'I would argue that education is the best long-term investment.', 'Stating a considered opinion'],
        ],
      },
    },
    {
      title: 'Common Abstract Themes with Model Answers',
      content: `<p>Familiarising yourself with recurring themes reduces panic when the examiner asks something unexpected. Below are model outlines for four common IELTS abstract themes.</p>`,
      points: [
        'Technology & Society: Discuss benefits (efficiency, connectivity) and drawbacks (isolation, misinformation). Use examples like social media or AI.',
        'Education: Compare traditional vs modern approaches. Mention access, equity, and the role of technology in the classroom.',
        'Environment: Balance economic development against sustainability. Reference specific policies or events (Paris Agreement, plastic bans).',
        'Culture: Explore globalisation vs local identity. Discuss how traditions evolve and whether cultural preservation is realistic.',
      ],
      examples: [
        { label: 'Technology', text: 'I think technology has made life more convenient, but it\'s also created new problems. For instance, while social media connects people across borders, it can also spread misinformation rapidly. On balance, I believe the benefits outweigh the risks, provided governments regulate responsibly.' },
        { label: 'Environment', text: 'In my view, environmental responsibility should be shared between governments and individuals. Governments can set regulations — for example, banning single-use plastics — but individuals also need to change daily habits like reducing energy consumption.' },
      ],
    },
  ],

  exercises: [
    {
      title: 'Identify Abstract vs Concrete Questions',
      type: 'select',
      instructions: 'Determine whether each question is abstract (society-level, opinion-based) or concrete (personal, factual).',
      questions: [
        {
          text: '"Do you think governments should invest more in public transport?"',
          options: ['Abstract', 'Concrete'],
          answer: 'Abstract',
          explanation: 'This asks for an opinion on policy — a society-level abstract question typical of Part 3.',
        },
        {
          text: '"What did you study at university?"',
          options: ['Abstract', 'Concrete'],
          answer: 'Concrete',
          explanation: 'This asks about your personal experience — a factual, concrete question typical of Part 1.',
        },
        {
          text: '"Why do some people prefer to live in the countryside rather than cities?"',
          options: ['Abstract', 'Concrete'],
          answer: 'Abstract',
          explanation: 'This asks you to discuss reasons for a general trend, requiring abstract reasoning about "some people" rather than your own choice.',
        },
        {
          text: '"How has the role of women in the workplace changed over time?"',
          options: ['Abstract', 'Concrete'],
          answer: 'Abstract',
          explanation: 'This is a broad societal question about historical change — abstract and discussion-oriented.',
        },
      ],
    },
    {
      title: 'Complete Abstract Answers',
      type: 'fill-in',
      instructions: 'Fill in the blank to complete the answer structure (Position → Reason → Example → Balance).',
      questions: [
        {
          text: 'I believe traditional customs should be preserved ___ they help communities maintain their identity.',
          answer: 'because',
          explanation: '"Because" connects the position (customs should be preserved) to the reason (maintaining identity), completing the Position → Reason step.',
        },
        {
          text: 'Remote work is becoming more popular. ___, many tech companies now allow employees to work from anywhere in the world.',
          answer: 'For instance',
          explanation: '"For instance" introduces the example step, providing concrete evidence to support the claim about remote work.',
        },
        {
          text: 'Free university education sounds ideal. ___, it would place an enormous financial burden on taxpayers.',
          answer: 'Having said that',
          explanation: '"Having said that" introduces the balance step, acknowledging a counterargument after supporting the main position.',
        },
        {
          text: '___ the government were to ban cars from city centres, air quality would improve dramatically.',
          answer: 'If',
          explanation: '"If" starts a standard hypothetical conditional (If + past simple, would + verb), used to speculate about a policy scenario.',
        },
      ],
    },
    {
      title: 'Choose the Strongest Argument Structure',
      type: 'select',
      instructions: 'Select the response that best follows the Position → Reason → Example → Balance structure.',
      questions: [
        {
          text: 'Question: "Should children learn a foreign language at primary school?"',
          options: [
            'Yes, I think so. Languages are good. My friend speaks French. But some people disagree.',
            'I strongly believe children should start learning a foreign language early because their brains are more receptive at that age. For example, in Sweden, children begin English lessons at age seven and typically reach fluency by their teens. That said, I understand some argue the curriculum is already overcrowded.',
            'Languages are important in today\'s world and children need them.',
          ],
          answer: 'I strongly believe children should start learning a foreign language early because their brains are more receptive at that age. For example, in Sweden, children begin English lessons at age seven and typically reach fluency by their teens. That said, I understand some argue the curriculum is already overcrowded.',
          explanation: 'This answer clearly follows Position (believe early learning) → Reason (receptive brains) → Example (Sweden) → Balance (overcrowded curriculum). The others lack development or structure.',
        },
        {
          text: 'Question: "Is it better to live in a big city or a small town?"',
          options: [
            'Big cities are better because there are more jobs.',
            'I think there are advantages to both, but personally I would choose a city because of the career opportunities. For instance, most multinational companies are headquartered in major cities. On the other hand, small towns offer a quieter lifestyle and stronger community bonds.',
            'Small towns are nice. Cities are noisy. I don\'t know really.',
          ],
          answer: 'I think there are advantages to both, but personally I would choose a city because of the career opportunities. For instance, most multinational companies are headquartered in major cities. On the other hand, small towns offer a quieter lifestyle and stronger community bonds.',
          explanation: 'This answer presents a nuanced position, gives a reason, provides an example, and balances with the opposing view — the full PREB structure.',
        },
        {
          text: 'Question: "Do you think technology will replace teachers in the future?"',
          options: [
            'Technology is everywhere these days.',
            'No, because teachers are important.',
            'I don\'t think technology will fully replace teachers, mainly because education involves emotional support and motivation that machines can\'t replicate. For example, during the pandemic, many students struggled with online-only learning despite having access to the best platforms. However, I do believe AI tools will increasingly assist teachers by personalising lesson content.',
          ],
          answer: 'I don\'t think technology will fully replace teachers, mainly because education involves emotional support and motivation that machines can\'t replicate. For example, during the pandemic, many students struggled with online-only learning despite having access to the best platforms. However, I do believe AI tools will increasingly assist teachers by personalising lesson content.',
          explanation: 'Clear position, reasoned argument, timely example (pandemic), and a balanced concession about AI assistance — an ideal Band 7+ response.',
        },
        {
          text: 'Question: "Whose responsibility is it to protect the environment?"',
          options: [
            'In my view, environmental protection is a shared responsibility. Governments have the power to enact legislation — for instance, the EU\'s ban on single-use plastics has significantly reduced waste. At the same time, individuals must also make conscious choices, such as reducing energy consumption at home.',
            'Everyone should protect the environment because it is important for the future.',
            'Governments should do it. They have the money.',
          ],
          answer: 'In my view, environmental protection is a shared responsibility. Governments have the power to enact legislation — for instance, the EU\'s ban on single-use plastics has significantly reduced waste. At the same time, individuals must also make conscious choices, such as reducing energy consumption at home.',
          explanation: 'This answer provides a clear position (shared responsibility), specific examples (EU ban, reducing energy), and balances two perspectives (government vs individual).',
        },
      ],
    },
    {
      title: 'Respond to an Abstract Question',
      type: 'writing',
      instructions: 'Write a full answer (4–6 sentences) using the Position → Reason → Example → Balance structure. Include at least one hypothetical sentence.',
      questions: [
        {
          text: '"Do you think online education will eventually replace traditional classrooms?"',
          example: '"I don\'t believe online education will completely replace traditional classrooms, mainly because face-to-face interaction is essential for developing social skills. If students only learned online, they would miss out on collaborative activities and peer relationships. For instance, university seminars rely on group discussion, which is difficult to replicate virtually. Having said that, I think a hybrid model — combining online flexibility with in-person sessions — could become the standard in the future."',
          tip: 'Use the PREB structure: Position first, then Reason, Example, and Balance. Include a hypothetical ("If students only learned online...").',
        },
        {
          text: '"How important is it for countries to preserve their cultural traditions?"',
          example: '"I think preserving cultural traditions is extremely important because they give communities a sense of identity and continuity. For example, in Japan, festivals like Obon have been celebrated for centuries and continue to bring families together. Suppose a country abandoned all its traditions — I believe people would feel a loss of belonging. On the other hand, traditions must also evolve; forcing outdated practices on younger generations could do more harm than good."',
          tip: 'Blend a concrete country/festival example with a hypothetical "Suppose..." scenario to show grammatical range.',
        },
        {
          text: '"Should governments prioritise economic growth or environmental protection?"',
          example: '"In my view, governments should aim to balance both, although environmental protection should take priority in the long term. The main reason is that economic growth is meaningless if the planet becomes uninhabitable. For instance, China\'s rapid industrialisation brought wealth but also severe pollution. Were governments to invest more in green technology now, they could create jobs while also reducing emissions."',
          tip: 'Use a formal hypothetical inversion ("Were governments to...") to demonstrate advanced grammar. Acknowledge both sides before taking a position.',
        },
      ],
    },
    {
      title: 'Match Hypothetical Language',
      type: 'select',
      instructions: 'Choose the correct hypothetical structure to complete each sentence.',
      questions: [
        {
          text: '___ public transport were free, fewer people would drive to work.',
          options: ['If', 'Although', 'Because'],
          answer: 'If',
          explanation: '"If" introduces a second conditional (If + past simple, would + verb) for an unreal present/future scenario.',
        },
        {
          text: '___ everyone switched to renewable energy, carbon emissions could fall by half.',
          options: ['Despite', 'Suppose', 'Unless'],
          answer: 'Suppose',
          explanation: '"Suppose" introduces a hypothetical scenario, inviting the listener to imagine a possibility.',
        },
        {
          text: '___ the government to invest in cycling infrastructure, more people would leave their cars at home.',
          options: ['If', 'Were', 'Should'],
          answer: 'Were',
          explanation: '"Were the government to..." is a formal conditional inversion, equivalent to "If the government invested..." — impressive in Part 3.',
        },
      ],
    },
  ],

  tips: [
    'Always start with a clear position — don\'t hedge so much that the examiner can\'t identify your view.',
    'Use the PREB framework (Position → Reason → Example → Balance) to stay organised.',
    'Practise hypothetical structures until they feel natural — examiners look for grammatical range.',
    'Don\'t panic if you know little about the topic — use general reasoning and personal experience.',
    'Aim to speak for 30–45 seconds per Part 3 answer to show you can develop ideas at length.',
  ],
  dailyPractice: [
    'Read one opinion article per day and practise summarising the argument using the PREB structure aloud.',
    'Pick a random abstract topic (education, technology, environment) and record a 45-second response.',
    'Practise converting concrete statements into hypothetical ones: "Many people drive to work" → "If petrol were more expensive, fewer people would drive."',
    'Watch a TED talk and note how the speaker structures arguments — identify their position, reasons, examples, and concessions.',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Abstract Topics: This course prepares IELTS candidates for Part 3 abstract discussion questions covering society, culture, education, and the environment. Teaches the PREB structure (Position-Reason-Example-Balance), hypothetical language, and how to handle unfamiliar topics with confidence. Targets Band 7+ in Fluency & Coherence and Grammatical Range.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...abstractTopicsData,
      title: abstractTopicsData.title + ' (Foundations)',
      overview: { ...abstractTopicsData.overview, duration: '15-20 minutes' },
      sections: abstractTopicsData.sections.slice(0, Math.max(2, Math.ceil(abstractTopicsData.sections.length / 2))),
      exercises: abstractTopicsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...abstractTopicsData,
      title: abstractTopicsData.title + ' (Advanced)',
      overview: { ...abstractTopicsData.overview, duration: '30-35 minutes' },
      sections: abstractTopicsData.sections,
      exercises: abstractTopicsData.exercises.map(ex => ({ ...ex, title: ex.title + ' (Advanced)' })),
      tips: [
        ...(abstractTopicsData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return abstractTopicsData;
}
